import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardShell } from '../dashboardShared/components/DashboardShell';
import { CommandHeader } from '../dashboardShared/components/CommandHeader';
import { KpiStrip } from '../dashboardShared/components/KpiStrip';
import { WeeklyBarChart } from '../dashboardShared/components/WeeklyBarChart';
import { AttentionList } from '../dashboardShared/components/AttentionList';
import { ActivityList } from '../dashboardShared/components/ActivityList';
import { JumpToList } from '../dashboardShared/components/JumpToList';
import { bucketIntoWeeks } from '../dashboardShared/weeklyBuckets';
import type { KpiCellData, JumpToRow } from '../dashboardShared/types';
import { deriveAdminAttention } from './deriveAdminAttention';
import { deriveAdminActivity, failedJobsSourceNote, relativeTime } from './deriveAdminActivity';
import { useLocale } from '../../contexts/LocaleContext';
import {
  useAdminSystemStats,
  useOrganizationStats,
  useAdminBillingStats,
  useStagedProductStats,
  useAdminRecentJobs,
  useVendorApprovalPending,
  useApiLatency,
} from './useAdminHomeData';

const P95_TARGET_MS = 300;

export const AdminHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { formatNumber } = useLocale();
  const formatMs = (ms: number) => `${formatNumber(ms)}ms`;

  const systemStatsQuery = useAdminSystemStats(true);
  const organizationStatsQuery = useOrganizationStats(true);
  const billingStatsQuery = useAdminBillingStats(true);
  const stagedStatsQuery = useStagedProductStats(true);
  const recentJobsQuery = useAdminRecentJobs(true);
  const vendorApprovalQuery = useVendorApprovalPending(true);
  const latencyQuery = useApiLatency(true);

  const systemStats = systemStatsQuery.data || null;
  const organizationStats = organizationStatsQuery.data || null;
  const billingStats = billingStatsQuery.data || null;
  const stagedStats = stagedStatsQuery.data || null;
  const recentJobs = recentJobsQuery.data || [];
  const pendingVendors = vendorApprovalQuery.data || [];
  const latency = latencyQuery.data || { p95Ms: null, sampleCount: 0 };

  const loading = systemStatsQuery.isLoading || organizationStatsQuery.isLoading || stagedStatsQuery.isLoading;

  const totalUsers = systemStats?.stats?.totalUsers ?? systemStats?.totalUsers ?? 0;
  const newUsersThisWeek = systemStats?.stats?.newUsersThisWeek ?? 0;
  const totalProducts = systemStats?.stats?.totalProducts ?? systemStats?.totalProducts ?? systemStats?.stats?.totalCameras ?? systemStats?.totalCameras ?? 0;

  // The Staged Products page's "Needs Review" tab (`review`). This used to read
  // `pending`, which showed 0 while thousands of products waited in review (MOT-85).
  const stagedReview = stagedStats?.review ?? 0;

  const failedJobsLast24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return recentJobs.filter((j) => j.status === 'failed' && new Date(j.completedAt || j.createdAt).getTime() >= cutoff);
  }, [recentJobs]);

  const dominantFailedJobVendor = useMemo(() => {
    if (failedJobsLast24h.length === 0) return null;
    const counts = new Map<string, number>();
    for (const job of failedJobsLast24h) {
      const name = job.brandName || 'Unknown source';
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
  }, [failedJobsLast24h]);

  // Stores, paying stores and MRR all come from one server-side definition
  // (`services/adminMetrics.ts`), shared with the Stores, Billing and Plans pages.
  const storeCount = billingStats?.storeCount ?? organizationStats?.cameraStores ?? 0;
  const monthlyRevenue = billingStats?.monthlyRevenue ?? 0;
  const storesWithoutPaidPlan = billingStats?.storesWithoutPaidPlan ?? 0;

  // Vendor organizations in `active` status (not the approval queue, not rejected or suspended).
  const activeVendors = organizationStats?.activeVendors ?? 0;

  const kpis: KpiCellData[] = [
    { id: 'total_products', label: 'Total products', value: totalProducts.toLocaleString(), delta: '', note: 'Every status: active, archived, discontinued', flag: 'none' },
    { id: 'needs_review', label: 'Needs review', value: stagedReview.toLocaleString(), delta: stagedStats?.oldestReviewAgeDays != null ? `${stagedStats.oldestReviewAgeDays}d oldest` : '', note: stagedStats?.remediating ? `${stagedStats.remediating.toLocaleString()} more remediating` : 'Staged products · blocks publishing', flag: stagedReview > 0 ? 'red' : 'none' },
    { id: 'jobs_failed', label: 'Jobs failed (24h)', value: failedJobsLast24h.length.toLocaleString(), delta: '', note: failedJobsSourceNote(failedJobsLast24h), flag: failedJobsLast24h.length > 0 ? 'red' : 'none' },
    { id: 'vendors', label: 'Vendors', value: activeVendors.toLocaleString(), delta: pendingVendors.length > 0 ? `+${pendingVendors.length} pending` : '', note: 'Active vendors', flag: pendingVendors.length > 0 ? 'amber' : 'none' },
    { id: 'stores', label: 'Stores', value: storeCount.toLocaleString(), delta: '', note: 'Active and pending store organizations', flag: 'none' },
    { id: 'total_users', label: 'Total users', value: totalUsers.toLocaleString(), delta: newUsersThisWeek > 0 ? `+${newUsersThisWeek} wk` : '', note: 'Registered platform accounts', flag: 'none' },
    {
      id: 'mrr',
      label: 'MRR',
      value: `$${Math.round(monthlyRevenue).toLocaleString()}`,
      delta: billingStats?.payingSubscriptions ? `${billingStats.payingSubscriptions} paying` : '',
      note: monthlyRevenue === 0 ? 'No paying stores yet' : 'Monthly recurring revenue',
      flag: monthlyRevenue === 0 ? 'muted' : 'none',
    },
    {
      id: 'api_p95',
      label: 'API p95',
      value: latency.p95Ms != null ? formatMs(latency.p95Ms) : '—',
      delta: latency.p95Ms != null ? `target ${formatMs(P95_TARGET_MS)}` : '',
      note: latency.p95Ms == null ? 'No data yet' : latency.p95Ms > P95_TARGET_MS ? 'Degraded' : 'Healthy',
      flag: latency.p95Ms != null && latency.p95Ms > P95_TARGET_MS ? 'amber' : 'none',
    },
  ];

  const attentionItems = useMemo(
    () =>
      deriveAdminAttention({
        stagedReviewCount: stagedReview,
        oldestReviewAgeDays: stagedStats?.oldestReviewAgeDays ?? null,
        failedJobsLast24h: failedJobsLast24h.length,
        dominantFailedJobVendor,
        pendingVendorApprovals: pendingVendors.map((v) => ({ vendorName: v.vendorName, skuCount: v.skuCount })),
        p95Ms: latency.p95Ms,
        p95TargetMs: P95_TARGET_MS,
        storesWithoutPaidPlan,
        monthlyRevenue,
      }),
    [stagedReview, stagedStats, failedJobsLast24h, dominantFailedJobVendor, pendingVendors, latency, storesWithoutPaidPlan, monthlyRevenue]
  );

  // No server-side weekly aggregation exists anywhere in the app (checked ingestion, jobs and
  // billing routes) — bucket the last 12 weeks of job history client-side. `result.productsCreated`
  // is the net-new catalog growth for completed jobs — price/stock refresh runs correctly count as
  // 0 here even though they "process" the whole existing catalog. Jobs still in flight fall back to
  // in-flight progress so they register on the chart before they complete.
  const chartWeeks = useMemo(
    () =>
      bucketIntoWeeks(
        recentJobs.map((j) => ({
          at: j.createdAt,
          count: j.result?.productsCreated ?? (j.status === 'running' || j.status === 'pending' ? j.progress?.current ?? 0 : 0),
          failed: j.status === 'failed',
        })),
        12
      ),
    [recentJobs]
  );

  const activityRows = useMemo(() => deriveAdminActivity(pendingVendors, recentJobs), [pendingVendors, recentJobs]);

  const jumpToRows: JumpToRow[] = [
    { id: 'staged', label: 'Staged products', meta: `${stagedReview.toLocaleString()} need review`, href: '/app/admin/staged-products' },
    { id: 'brands-vendors', label: 'Brands & vendors', meta: `${activeVendors.toLocaleString()} active`, href: '/app/admin/brands-vendors' },
    { id: 'jobs', label: 'Job management', meta: `${failedJobsLast24h.length} failed`, href: '/app/admin/jobs' },
    { id: 'ingestion', label: 'Data ingestion', meta: '', href: '/app/admin/pricing-ingestion' },
    { id: 'audit-log', label: 'Audit log', meta: '', href: '/app/admin/audit-logs' },
  ];

  return (
    <DashboardShell
      header={
        <CommandHeader
          roleBadge="Admin"
          orgName="Platform overview"
          headlineValue={totalProducts.toLocaleString()}
          headlineLabel="Products in the platform catalog (all statuses)"
          subline={`${storeCount.toLocaleString()} stores · ${activeVendors.toLocaleString()} vendors · ${totalUsers.toLocaleString()} users · ${stagedReview.toLocaleString()} staged products need review`}
          chips={[
            { label: 'Needs review', value: stagedReview.toLocaleString(), dot: stagedReview > 0 ? 'red' : 'green' },
            { label: 'Vendors pending', value: pendingVendors.length.toLocaleString(), dot: pendingVendors.length > 0 ? 'amber' : 'green' },
            { label: 'API p95', value: latency.p95Ms != null ? formatMs(latency.p95Ms) : '—', dot: latency.p95Ms != null && latency.p95Ms > P95_TARGET_MS ? 'amber' : 'green' },
            { label: 'Jobs failed 24h', value: failedJobsLast24h.length.toLocaleString(), dot: failedJobsLast24h.length > 0 ? 'red' : 'green' },
          ]}
          ctaLabel="Open admin panel"
          onCtaClick={() => navigate('/app/admin')}
          timestamp={`Health checked ${relativeTime(latencyQuery.dataUpdatedAt)}`}
          loading={loading}
        />
      }
      kpiStrip={<KpiStrip kpis={kpis} loading={loading} columns={4} />}
      chart={
        <WeeklyBarChart
          title="Products ingested per week"
          totalLabel={`${totalProducts.toLocaleString()} total`}
          note="Last 12 weeks · ingestion is the growth metric while revenue is pre-launch"
          weeks={chartWeeks}
          markLowAsFailure={false}
          loading={recentJobsQuery.isLoading}
        />
      }
      attention={<AttentionList items={attentionItems.slice(0, 5)} totalCount={attentionItems.length} loading={loading} />}
      activity={<ActivityList title="Recent platform activity" rows={activityRows} loading={recentJobsQuery.isLoading} />}
      jumpTo={<JumpToList rows={jumpToRows} />}
    />
  );
};
